// src/pages/commercial/CommercialPhoningPage.js
import React, { useEffect, useState } from 'react';
import CommercialLayout from '../../components/commercial/CommercialLayout';
import { useAuth } from '../../contexts/AuthContext';
import pb from '../../lib/pocketbase';

export default function CommercialPhoningPage() {
    const { user } = useAuth();
    const [leads, setLeads] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [currentNote, setCurrentNote] = useState('');
    const [script, setScript] = useState('');
    const [templates, setTemplates] = useState([]);

    // Fetch leads (customers created recently or specifically marked as 'lead' if that field existed, here we take all)
    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            try {
                const [customersRes, templatesRes] = await Promise.all([
                    pb.collection('customers').getFullList({
                        sort: '-created',
                        filter: `created_by = "${user.id}" || commercial_id = "${user.id}"`
                    }),
                    pb.collection('sales_templates').getFullList({
                        filter: `category = "script" || category = "other"`
                    }).catch(() => []) // Silent fail if collection doesn't exist yet
                ]);

                setLeads(customersRes);
                setTemplates(templatesRes);
                if (templatesRes.length > 0) setScript(templatesRes[0].content);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [user.id]);

    const handleNext = () => {
        if (currentIndex < leads.length - 1) {
            setCurrentIndex(prev => prev + 1);
            setCurrentNote('');
        }
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(prev => prev - 1);
            setCurrentNote('');
        }
    };

    const handleSaveNote = async () => {
        if (!currentNote.trim()) return;
        const lead = leads[currentIndex];
        try {
            // Append note to existing notes (simple append for now)
            const newNotes = (lead.notes || '') + '\n' + `[${new Date().toLocaleDateString()}] Phoning: ${currentNote}`;
            await pb.collection('customers').update(lead.id, { notes: newNotes });

            // Update local state
            const updatedLeads = [...leads];
            updatedLeads[currentIndex] = { ...lead, notes: newNotes };
            setLeads(updatedLeads);

            alert('Note enregistrée !');
            setCurrentNote('');
        } catch (err) {
            alert('Erreur sauvegarde');
        }
    };

    const currentLead = leads[currentIndex];

    // Quick calculate score locally for display
    const getScore = (client) => {
        let s = 0;
        if (client.email) s += 25;
        if (client.phone) s += 25;
        if (client.company) s += 20;
        return s;
    };

    return (
        <CommercialLayout title="Session Phoning">
            <div className="commercial-page-wrapper" style={{ height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
                <style>{`
                .commercial-page-wrapper {
                    --admin-panel: var(--commercial-panel, #fff);
                    --admin-panel-2: var(--commercial-panel-2, #f9fafb);
                    --admin-border: var(--commercial-border, #e2e8f0);
                    --admin-text: var(--commercial-text, #0f172a);
                    --admin-muted: var(--commercial-muted, #64748b);
                    --admin-accent: var(--commercial-accent, #4f46e5);
                }
                .phoning-grid {
                    display: grid; grid-template-columns: 350px 1fr; gap: 24px; flex: 1; min-height: 0;
                }
                .panel-card {
                    background: var(--admin-panel); border: 1px solid var(--admin-border); border-radius: 12px;
                    display: flex; flexDirection: column; overflow: hidden;
                }
                .lead-info-row {
                    display: flex; gap: 12px; align-items: center; margin-bottom: 12px;
                }
                .lead-icon { width: 32px; height: 32px; background: var(--admin-panel-2); border-radius: 6px; display: grid; place-items: center; }
                
                .script-selector {
                    margin-bottom: 16px; width: 100%; padding: 8px; border-radius: 8px; border: 1px solid var(--admin-border); background: var(--admin-panel-2); color: var(--admin-text);
                }
                .action-bar {
                    margin-top: auto; padding-top: 24px; display: flex; gap: 12px;
                }
                .btn-nav {
                    flex: 1; padding: 12px; border-radius: 8px; font-weight: 600; cursor: pointer; border: 1px solid var(--admin-border); background: var(--admin-panel); color: var(--admin-text);
                }
                .btn-nav:hover:not(:disabled) { background: var(--admin-panel-2); }
                .btn-nav:disabled { opacity: 0.5; cursor: not-allowed; }
                
                .btn-call {
                     flex: 2; background: #10b981; color: white; border: none; font-size: 1.1rem;
                }
                .btn-call:hover { filter: brightness(1.1); }

                @media (max-width: 900px) {
                    .phoning-grid { grid-template-columns: 1fr; overflow-y: auto; }
                }
                `}</style>

                {loading ? (
                    <div style={{ padding: 40, textAlign: 'center' }}>Chargement des leads...</div>
                ) : leads.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center' }}>Aucun lead trouvé.</div>
                ) : (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                            <div style={{ fontWeight: 600, color: 'var(--admin-muted)' }}>
                                Lead {currentIndex + 1} / {leads.length}
                            </div>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button className="btn-nav" style={{ padding: '6px 12px' }} onClick={handlePrev} disabled={currentIndex === 0}>← Précédent</button>
                                <button className="btn-nav" style={{ padding: '6px 12px' }} onClick={handleNext} disabled={currentIndex === leads.length - 1}>Suivant →</button>
                            </div>
                        </div>

                        <div className="phoning-grid">

                            {/* Left Panel: Lead Info */}
                            <div className="panel-card" style={{ padding: 24 }}>
                                <div style={{ fontSize: '2rem', marginBottom: 16, width: 80, height: 80, background: 'var(--admin-panel-2)', borderRadius: 40, display: 'grid', placeItems: 'center', margin: '0 auto 24px auto', border: '2px solid var(--admin-border)' }}>
                                    {currentLead.name.charAt(0).toUpperCase()}
                                </div>
                                <h2 style={{ textAlign: 'center', margin: '0 0 8px 0' }}>{currentLead.name}</h2>
                                <p style={{ textAlign: 'center', color: 'var(--admin-muted)', marginTop: 0 }}>{currentLead.company || 'Particulier'}</p>

                                <div style={{ margin: '24px 0', display: 'flex', flexDirection: 'column', gap: 16 }}>
                                    {currentLead.phone ? (
                                        <div className="lead-info-row" style={{ background: '#ecfdf5', color: '#059669', padding: 12, borderRadius: 8 }}>
                                            <span style={{ fontSize: '1.2rem' }}>📞</span>
                                            <span style={{ fontWeight: 700, fontSize: '1.1rem' }}>{currentLead.phone}</span>
                                        </div>
                                    ) : (
                                        <div className="lead-info-row" style={{ opacity: 0.5 }}>
                                            <span>📞</span> <span>Pas de numéro</span>
                                        </div>
                                    )}
                                    <div className="lead-info-row">
                                        <span>📧</span>
                                        <a href={`mailto:${currentLead.email}`} style={{ color: 'var(--admin-accent)' }}>{currentLead.email}</a>
                                    </div>
                                    <div className="lead-info-row">
                                        <span>📍</span> <span>{currentLead.address || 'Adresse inconnue'}</span>
                                    </div>
                                    <div className="lead-info-row">
                                        <span>🔥</span> <span>Score: {getScore(currentLead)}/70</span>
                                    </div>
                                </div>

                                <div style={{ marginTop: 'auto', background: 'var(--admin-panel-2)', padding: 12, borderRadius: 8, fontSize: '0.85rem', color: 'var(--admin-muted)', maxHeight: 150, overflowY: 'auto' }}>
                                    <strong>Notes existantes:</strong><br />
                                    {currentLead.notes || 'Aucune note.'}
                                </div>
                            </div>

                            {/* Right Panel: Script & Actions */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                                <div className="panel-card" style={{ flex: 2, padding: 24 }}>
                                    <h3 style={{ marginTop: 0 }}>Script d'appel</h3>
                                    {templates.length > 0 && (
                                        <select className="script-selector" onChange={(e) => setScript(e.target.value)}>
                                            {templates.map(t => (
                                                <option key={t.id} value={t.content}>{t.title}</option>
                                            ))}
                                        </select>
                                    )}
                                    <div style={{ flex: 1, whiteSpace: 'pre-wrap', lineHeight: 1.6, overflowY: 'auto', padding: 16, background: 'var(--admin-panel-2)', borderRadius: 8, border: '1px solid var(--admin-border)' }}>
                                        {script || "Aucun script disponible. Ajoutez-en dans la section 'Modèles'."}
                                    </div>
                                </div>

                                <div className="panel-card" style={{ flex: 1, padding: 24 }}>
                                    <h3 style={{ marginTop: 0 }}>Prise de note rapide</h3>
                                    <textarea
                                        style={{ width: '100%', height: '100%', resize: 'none', border: 'none', background: 'transparent', outline: 'none', fontSize: '1rem', color: 'var(--admin-text)' }}
                                        placeholder="Notez le résultat de l'appel ici..."
                                        value={currentNote}
                                        onChange={e => setCurrentNote(e.target.value)}
                                    />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                                        <button className="btn-nav" style={{ flex: 0, padding: '8px 24px', background: 'var(--admin-accent)', color: 'white', border: 'none' }} onClick={handleSaveNote}>
                                            Enregistrer Note
                                        </button>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </>
                )}
            </div>
        </CommercialLayout>
    );
}
